import React, { useMemo } from 'react';
import { useAuth } from '../AuthContext';
import { UserProfile } from '../types';
import BackButton from '../components/BackButton';

interface BlockedUserItemProps {
    user: UserProfile;
    onUnblock: (uid: string) => void;
}

const BlockedUserItem: React.FC<BlockedUserItemProps> = ({ user, onUnblock }) => (
    <div className="flex items-center p-3 space-x-3 bg-white rounded-lg shadow-sm">
        <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full object-cover" />
        <div className="flex-1 min-w-0">
            <p className="font-bold truncate">{user.displayName}</p>
        </div>
        <button
            onClick={() => onUnblock(user.uid)}
            className="bg-gray-200 text-gray-700 font-bold text-xs px-3 py-1 rounded-md hover:bg-gray-300"
        >
            Unblock
        </button>
    </div>
);

const BlockedUsersPage: React.FC = () => {
    const { userProfile, allUsers, toggleBlockUser } = useAuth();

    const blockedUsers = useMemo(() => {
        if (!userProfile?.blockedUsers) return [];
        return allUsers.filter(u => userProfile.blockedUsers!.includes(u.uid));
    }, [userProfile, allUsers]);

    return (
        <div className="space-y-4">
            <BackButton text="Back to Settings" />
            <h2 className="text-2xl font-bold">
                <span className="aurora-text">Blocked Users</span>
            </h2>
            <p className="text-sm text-gray-500">
                Users you block will not be able to see your profile or posts, and you will not see theirs. They will not be notified.
            </p>

            <div className="space-y-2">
                {blockedUsers.length > 0 ? (
                    blockedUsers.map(user => (
                        <BlockedUserItem key={user.uid} user={user} onUnblock={toggleBlockUser} />
                    ))
                ) : (
                    <p className="text-center text-gray-400 pt-8">You haven't blocked any users.</p>
                )}
            </div>
        </div>
    );
};

export default BlockedUsersPage;